/**
 * ConversionTrendPanel — Compact time-series WON vs LOST with PoP momentum
 *
 * Optimized: reduced padding, inline legend, smaller delta badges, tighter bars
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  TrendingUp, TrendingDown, Calendar, Loader2, BarChart3,
  ArrowUp, ArrowDown, Minus
} from 'lucide-react';
import api from '../utils/api';


const IntervalToggle = ({ value, onChange }) => {
  const { t } = useTranslation();
  return (
    <div className="flex bg-slate-100 rounded p-0.5" data-testid="interval-toggle">
      {[
        { v: 'weekly', labelKey: 'provider.convTrend.weekly' },
        { v: 'monthly', labelKey: 'provider.convTrend.monthly' },
      ].map(({ v, labelKey }) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
            value === v ? 'bg-white shadow-sm text-slate-800 font-medium' : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid={`interval-${v}`}
        >
          {t(labelKey)}
        </button>
      ))}
    </div>
  );
};

const fmt = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const DeltaBadge = ({ value, label, testId }) => {
  const { t } = useTranslation();
  if (value === null || value === undefined) return null;
  const isPos = value > 0;
  const isNeg = value < 0;
  const Icon = isPos ? ArrowUp : isNeg ? ArrowDown : Minus;
  const color = isPos ? 'text-emerald-600' : isNeg ? 'text-red-500' : 'text-slate-400';
  const bg = isPos ? 'bg-emerald-50 border-emerald-200' : isNeg ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200';

  return (
    <span
      className={`inline-flex items-center gap-px px-1 py-px rounded-full text-[9px] font-semibold border ${bg} ${color}`}
      title={t('provider.convTrend.deltaTitle', { sign: value > 0 ? '+' : '', value, label })}
      data-testid={testId}
    >
      <Icon className="h-2 w-2" />
      {value > 0 ? '+' : ''}{value}%
    </span>
  );
};

const TrendIndicator = ({ series }) => {
  const { t } = useTranslation();
  const recent = series.slice(-4);
  if (recent.length < 2) return null;
  const first = recent[0].conversion_rate;
  const last = recent[recent.length - 1].conversion_rate;
  const trend = last - first;
  const isUp = trend > 0.01;
  const isDown = trend < -0.01;

  return (
    <div className="flex items-center gap-1" data-testid="trend-momentum">
      {isUp ? (
        <><TrendingUp className="h-3 w-3 text-emerald-500" /><span className="text-[10px] font-semibold text-emerald-600">{t('provider.convTrend.accelerating')}</span></>
      ) : isDown ? (
        <><TrendingDown className="h-3 w-3 text-red-500" /><span className="text-[10px] font-semibold text-red-500">{t('provider.convTrend.decelerating')}</span></>
      ) : (
        <><Minus className="h-3 w-3 text-slate-400" /><span className="text-[10px] font-medium text-slate-500">{t('provider.convTrend.stable')}</span></>
      )}
    </div>
  );
};

export const ConversionTrendPanel = ({ clinicId, filters = {}, onSummaryUpdate }) => {
  const { t } = useTranslation();
  const [interval, setInterval] = useState('weekly');
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!clinicId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ clinic_id: clinicId, interval });
      if (filters.source) params.append('source', filters.source);
      if (filters.assigned_to) params.append('assigned_to', filters.assigned_to);
      const res = await api.get(`/v2/analytics/conversion-timeseries?${params.toString()}`);
      {
        const data = res.data;
        setSeries(data.series || []);
      }
    } catch (err) {
      console.error('Conversion timeseries fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [clinicId, interval, filters.source, filters.assigned_to]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalWon = series.reduce((s, d) => s + d.won_count, 0);
  const totalLost = series.reduce((s, d) => s + d.lost_count, 0);
  const overallRate = totalWon + totalLost > 0
    ? ((totalWon / (totalWon + totalLost)) * 100).toFixed(1)
    : '0.0';
  const maxBar = Math.max(...series.map((d) => d.won_count + d.lost_count), 1);
  const intervalLabel = interval === 'weekly' ? t('provider.convTrend.labelWeek') : t('provider.convTrend.labelMonth');

  // Emit PoP delta for collapsed summary strip
  useEffect(() => {
    if (onSummaryUpdate && series.length >= 2) {
      const last = series[series.length - 1];
      onSummaryUpdate({ conversionDelta: last?.conversion_delta_vs_prev ?? null });
    }
  }, [series, onSummaryUpdate]);

  return (
    <Card data-testid="conversion-trend-panel">
      <CardContent className="px-4 py-3">
        {/* Header: title + trend + legend + toggle */}
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
            <span className="text-sm font-semibold text-slate-800">{t('provider.convTrend.title')}</span>
          </div>
          {!loading && series.length >= 2 && <TrendIndicator series={series} />}

          {/* Inline legend + rate */}
          {!loading && series.length > 0 && (
            <div className="flex items-center gap-2 ml-auto" data-testid="trend-summary">
              <span className="flex items-center gap-1 text-[10px] text-slate-500">
                <span className="w-2 h-2 rounded-sm bg-emerald-500 inline-block" /> {t('provider.convTrend.won')} {totalWon}
              </span>
              <span className="flex items-center gap-1 text-[10px] text-slate-500">
                <span className="w-2 h-2 rounded-sm bg-red-400 inline-block" /> {t('provider.convTrend.lost')} {totalLost}
              </span>
              <Badge variant="outline" className="text-[10px] font-bold" data-testid="trend-overall-rate">
                {overallRate}%
              </Badge>
            </div>
          )}
          <IntervalToggle value={interval} onChange={setInterval} />
        </div>

        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-sky-500" />
          </div>
        ) : series.length === 0 ? (
          <div className="flex items-center gap-2 py-4 justify-center" data-testid="trend-empty">
            <BarChart3 className="h-5 w-5 text-slate-300" />
            <p className="text-xs text-slate-400">{t('provider.convTrend.empty')}</p>
          </div>
        ) : (
          <div className="space-y-0.5" data-testid="trend-chart">
            {series.map((d) => {
              const total = d.won_count + d.lost_count;
              const barWidth = Math.max((total / maxBar) * 100, 8);
              const wonPct = total > 0 ? (d.won_count / total) * 100 : 0;
              const ratePct = (d.conversion_rate * 100).toFixed(0);
              const hasDelta = d.conversion_delta_vs_prev !== null && d.conversion_delta_vs_prev !== undefined;

              return (
                <div key={d.period_start} className="flex items-center gap-2" data-testid={`trend-bar-${d.period_start}`}>
                  <div className="flex items-center gap-1 w-[72px] shrink-0 text-right">
                    <Calendar className="h-2.5 w-2.5 text-slate-400" />
                    <span className="text-[10px] text-slate-500 leading-none">{fmt(d.period_start)}</span>
                  </div>
                  <div className="flex-1 h-5 bg-slate-50 rounded overflow-hidden">
                    <div
                      className="h-full flex rounded overflow-hidden transition-all duration-500"
                      style={{ width: `${barWidth}%` }}
                    >
                      {d.won_count > 0 && (
                        <div className="h-full bg-emerald-500 flex items-center justify-center" style={{ width: `${wonPct}%` }}>
                          <span className="text-[9px] font-bold text-white px-0.5">{d.won_count}</span>
                        </div>
                      )}
                      {d.lost_count > 0 && (
                        <div className="h-full bg-red-400 flex items-center justify-center" style={{ width: `${100 - wonPct}%` }}>
                          <span className="text-[9px] font-bold text-white px-0.5">{d.lost_count}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <span className="text-[10px] font-semibold w-[28px] text-right shrink-0" data-testid={`trend-rate-${d.period_start}`}>
                    <span className={d.conversion_rate >= 0.5 ? 'text-emerald-600' : d.conversion_rate > 0 ? 'text-amber-600' : 'text-red-500'}>
                      {ratePct}%
                    </span>
                  </span>
                  {hasDelta && (
                    <DeltaBadge value={d.conversion_delta_vs_prev} label={intervalLabel} testId={`delta-conv-${d.period_start}`} />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ConversionTrendPanel;
